/**
 * Filter + search UI model for the redesigned CRL list view (REQ-12).
 *
 * The seven filter buckets are derived from `CrlItemStatus` (+ the overdue and
 * assigned-to-me derivations). Like the tasks page, the selection is held by
 * the container as a signal and applied to the selector-projected
 * `CrlDisplaySection[]` through a pure helper — no NGXS state for view UI.
 * Selection is persisted to `localStorage` under a VERSIONED key so a future
 * shape change can bump the version and reset cleanly instead of crashing on a
 * stale payload.
 */

import { CRL_IN_REVIEW_ENABLED, CrlItemStatus } from './cxp-crl-detail.model';

/**
 * Every CRL filter bucket the spec defines (REQ-12). Which of them the menu
 * actually offers is {@link CRL_FILTER_ORDER}'s call — `IN_REVIEW` is defined
 * here but only listed there while {@link CRL_IN_REVIEW_ENABLED} is on.
 *
 * Values are stable string ids — they are persisted to `localStorage`, so
 * renaming one is a breaking shape change that must bump
 * `CRL_FILTERS_STORAGE_KEY`.
 */
export enum CrlFilterKey {
  WAITING_FOR_YOU = 'waitingForYou',
  IN_REVIEW = 'inReview',
  NEEDS_UPDATE = 'needsUpdate',
  FINISHED = 'finished',
  NOT_APPLICABLE = 'notApplicable',
  OVERDUE = 'overdue',
  ASSIGNED_TO_ME = 'assignedToMe',
}

/**
 * Container-owned selection state — the set of currently active filter buckets.
 * An empty set means "no filter" (every item passes). Buckets are OR-combined:
 * an item is shown when it matches ANY active bucket.
 */
export type CrlFilterSelection = ReadonlySet<CrlFilterKey>;

/** A single filter option rendered in the multi-select menu, with its live count. */
export interface CrlFilterOption {
  key: CrlFilterKey;
  label: string;
  /** Items in the full request matching this bucket (does not react to other selections). */
  count: number;
  selected: boolean;
}

/**
 * The status bucket each filter key maps to, for the buckets that are exactly
 * one status. WAITING_FOR_YOU, OVERDUE and ASSIGNED_TO_ME are matched
 * separately in the helper — the first spans two statuses, the last two are
 * derived — so they are intentionally absent here.
 */
export const CRL_FILTER_STATUS: Readonly<Partial<Record<CrlFilterKey, CrlItemStatus>>> = {
  [CrlFilterKey.IN_REVIEW]: CrlItemStatus.IN_REVIEW,
  [CrlFilterKey.NEEDS_UPDATE]: CrlItemStatus.NEEDS_UPDATE,
  [CrlFilterKey.FINISHED]: CrlItemStatus.FINISHED,
  [CrlFilterKey.NOT_APPLICABLE]: CrlItemStatus.NOT_APPLICABLE,
};

/** Display labels for each bucket, reusing the REQ-12 wording. */
export const CRL_FILTER_LABELS: Readonly<Record<CrlFilterKey, string>> = {
  [CrlFilterKey.WAITING_FOR_YOU]: 'Waiting for You',
  [CrlFilterKey.IN_REVIEW]: 'In Review',
  [CrlFilterKey.NEEDS_UPDATE]: 'Needs Update',
  [CrlFilterKey.FINISHED]: 'Finished',
  [CrlFilterKey.NOT_APPLICABLE]: 'Marked as N/A',
  [CrlFilterKey.OVERDUE]: 'Overdue',
  [CrlFilterKey.ASSIGNED_TO_ME]: 'Assigned to Me',
};

/**
 * The buckets the Filters menu offers, in REQ-12 order. In Review is listed
 * only while {@link CRL_IN_REVIEW_ENABLED} surfaces that concept — without it
 * no item ever resolves to `IN_REVIEW`, so the bucket would always count 0.
 *
 * `VALID_FILTER_KEYS` (in `cxp-crl-filters.utils`) is built from this array, so
 * a bucket missing here is one the existing parse guard discards when it turns
 * up in a persisted selection — which is why hiding one needs no
 * `CRL_FILTERS_STORAGE_KEY` bump.
 */
export const CRL_FILTER_ORDER: readonly CrlFilterKey[] = [
  CrlFilterKey.WAITING_FOR_YOU,
  ...(CRL_IN_REVIEW_ENABLED ? [CrlFilterKey.IN_REVIEW] : []),
  CrlFilterKey.NEEDS_UPDATE,
  CrlFilterKey.FINISHED,
  CrlFilterKey.NOT_APPLICABLE,
  CrlFilterKey.OVERDUE,
  CrlFilterKey.ASSIGNED_TO_ME,
];

/** The overdue + needs-update preset the action-banner CTA toggles. */
export const CRL_ACTION_REQUIRED_FILTERS: readonly CrlFilterKey[] = [CrlFilterKey.OVERDUE, CrlFilterKey.NEEDS_UPDATE];

/**
 * Buckets whose item set is wholly contained by another bucket's. Selecting the
 * containing key makes the contained one redundant — the list would not change
 * either way, but a stale chip for the narrower bucket would still be offered
 * for removal, implying it were doing something.
 *
 * OVERDUE ⊂ WAITING_FOR_YOU because `deriveItemStatus` only ever promotes a
 * WAITING item to OVERDUE: every overdue item is a past-due waiting item. That
 * containment is what lets the Waiting for You bucket count the same items its
 * tile counts. NEEDS_UPDATE is deliberately NOT here — a past-due
 * changes-requested item keeps its NEEDS_UPDATE status, so it sits outside
 * Waiting for You entirely.
 */
export const CRL_FILTER_SUBSUMES: Readonly<Partial<Record<CrlFilterKey, readonly CrlFilterKey[]>>> = {
  [CrlFilterKey.WAITING_FOR_YOU]: [CrlFilterKey.OVERDUE],
};

/**
 * The three clickable metric tiles (PP-3642). Each maps to the EXISTING filter
 * bucket it toggles, so a tile click composes through the same `CrlFilterKey`
 * selection mechanism the Filters menu uses — no parallel filter state. The
 * completion-status tile is intentionally absent: it is a progress readout, not
 * a filter. "Waiting for You" maps to the WAITING bucket (overdue is folded into
 * that tile's count but stays its own bucket in the Filters menu).
 *
 * The map covers every metric tile the design defines, including `inReview`;
 * whether that tile renders is the tiles template's call, gated on
 * {@link CRL_IN_REVIEW_ENABLED}.
 */
export const CRL_TILE_FILTER_KEY: Readonly<Record<'waitingForYou' | 'inReview' | 'needsUpdate', CrlFilterKey>> = {
  waitingForYou: CrlFilterKey.WAITING_FOR_YOU,
  inReview: CrlFilterKey.IN_REVIEW,
  needsUpdate: CrlFilterKey.NEEDS_UPDATE,
};

/** A single "All Sections" dropdown option — `id: null` is the "All Sections" entry. */
export interface CrlSectionOption {
  /** Section id, or `null` for the "All Sections" reset entry. */
  id: number | null;
  /** Display label (`All Sections` for the reset entry, else `A. Income`). */
  label: string;
}

/**
 * Container-owned section scope (PP-3642). `null` means "All Sections" (no
 * scoping). Held as a container signal alongside the filter selection + search,
 * and applied through the same pure helper — view UI state, not NGXS.
 */
export type CrlSectionFilter = number | null;

/**
 * Versioned `localStorage` key for the persisted selection. Bump the `vN`
 * suffix whenever `CrlFilterKey` values or the serialized shape change so old
 * payloads are discarded by the parse guard rather than mis-applied.
 */
export const CRL_FILTERS_STORAGE_KEY = 'cxp.crl.filters.v1';
